import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useRecentSplit } from '@tunasong/search'
import { Button, EntityList, Typography, useFavorites, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef } from 'react'
import { useIsSoloUser } from './use-is-solo-user.js'
import { useSectionPinShortcut } from './use-section-pin-shortcut.js'
import styles from './merged-continue-section.module.css'

export interface MergedContinueSectionProps {
  /** Empty-state CTA — open the Spaces view. */
  onOpenSpaces(): void
  /** Empty-state CTA — start a new song. */
  onNewSong(): void
}

/**
 * Tiny-viewport collapse: fuses `Pinned` + `Continue working` into one section
 * labeled `Continue`. Pinned items render first (in pinOrder), then non-pinned
 * `mine`. Recently updated stays as its own section below to preserve the
 * collaborator-vs-mine distinction.
 */
export const MergedContinueSection: FC<MergedContinueSectionProps> = props => {
  const { onOpenSpaces, onNewSong } = props
  const { mine, isLoading: isLoadingSplit } = useRecentSplit()
  const { pinnedFavorites, isPinned } = useFavorites()
  const navigateToEntity = useNavigateToEntity()
  const isSolo = useIsSoloUser()
  const sectionRef = useRef<HTMLElement>(null)

  const pinnedIds = pinnedFavorites
    .map(f => f.entityId)
    .filter((id): id is string => typeof id === 'string')
  const mineIds = mine.map(e => e.id)
  const allIds = [...pinnedIds, ...mineIds.filter(id => !pinnedIds.includes(id))]

  const { existingEntities = [], isLoading: isResolving } = graphHooks.useEntitiesById(allIds)
  const entitiesById = new Map(existingEntities.map(e => [e.id, e]))

  const pinnedItems: Persisted<Entity>[] = pinnedIds
    .map(id => entitiesById.get(id))
    .filter((e): e is Persisted<Entity> => Boolean(e) && !e?.trash)

  const mineItems: Persisted<Entity>[] = mineIds
    .filter(id => !pinnedIds.includes(id))
    .map(id => entitiesById.get(id))
    .filter((e): e is Persisted<Entity> => Boolean(e) && !e?.trash)
    .filter(e => !isPinned(e.id))

  const items: Persisted<Entity>[] = [...pinnedItems, ...mineItems]
  const showHairline = pinnedItems.length > 0 && mineItems.length > 0

  const loading = isLoadingSplit || isResolving

  useSectionPinShortcut({ sectionRef, entities: items })

  if (!loading && items.length === 0) {
    return (
      <section ref={sectionRef} className={styles.section} aria-labelledby="dash-continue">
        <Typography id="dash-continue" className={styles.heading} variant="h2">
          Continue
        </Typography>
        <Typography className={styles.emptyHeading} variant="subtitle1">
          Nothing in progress yet.
        </Typography>
        <Typography className={styles.emptyBody} variant="caption">
          Open a Space or create a song to start.
        </Typography>
        <div className={styles.emptyActions}>
          <Button onClick={onOpenSpaces}>Open a Space</Button>
          <Button onClick={onNewSong}>New Song</Button>
        </div>
      </section>
    )
  }

  return (
    <section ref={sectionRef} className={styles.section} aria-labelledby="dash-continue">
      <Typography id="dash-continue" className={styles.heading} variant="h2">
        Continue
      </Typography>
      {pinnedItems.length > 0 ? (
        <EntityList
          showBreadcrumbs="inactiveLinks"
          entities={pinnedItems}
          onOpen={navigateToEntity}
          size="medium"
          actions={false}
          showLastEditor={!isSolo}
          itemAriaLabelPrefix="Pinned: "
        />
      ) : null}
      {showHairline ? <div data-testid="dash-hairline" aria-hidden className={styles.hairline} /> : null}
      {mineItems.length > 0 ? (
        <EntityList
          showBreadcrumbs="inactiveLinks"
          entities={mineItems}
          onOpen={navigateToEntity}
          size="medium"
          actions={false}
          showLastEditor={!isSolo}
        />
      ) : null}
    </section>
  )
}

export default MergedContinueSection
